/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Layers,
  Plus,
  Search,
  Edit2,
  Trash2,
  Copy,
  ChevronDown,
  Check,
  Building2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { subAccountService } from '../services/subAccountService';
import { accountService } from '../services/accountService';
import { useAuth } from '../context/AuthContext';
import toastService from '../utils/toastService';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import { usePermissions } from '../context/PermissionContext';
import { toLocal } from '../utils/dateTime';

const SEARCH_DEBOUNCE_MS = 500;
const CLASS_BG_PRIMARY_MUTED = 'bg-primary/5';
const CLASS_TEXT_PRIMARY = 'text-primary';
const CLASS_TEXT_MUTED_HOVER = 'text-base-content/40 group-hover:text-primary';

const SubAccounts = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { user } = useAuth();
  const [subAccounts, setSubAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(user?.accountUID || null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSubAccount, setSelectedSubAccount] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    status: 'All'
  });
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const statusDropdownRef = useRef(null);
  const accountDropdownRef = useRef(null);
  const canShowActions = can.sub_accounts.create() || can.sub_accounts.update() || can.sub_accounts.delete();

  // Debounce search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setIsStatusOpen(false);
      }
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target)) {
        setIsAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch accounts if user is platform admin (to allow selecting an account)
  useEffect(() => {
    if (!user?.accountUID) {
      const fetchAccounts = async () => {
        try {
          const { data } = await accountService.getAccounts({ limit: 100 });
          const accountsList = data.data || data || [];
          setAccounts(accountsList);
          /* if (accountsList.length > 0 && !selectedAccountId) {
            setSelectedAccountId(accountsList[0].id);
          } */
        } catch (err) {
          toastService.error('Failed to load accounts');
        }
      };
      fetchAccounts();
    }
  }, [user?.accountUID, selectedAccountId]);

  const fetchSubAccounts = useCallback(async () => {
    const accountId = selectedAccountId || null;

    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearch || undefined,
        status: filters.status !== 'All' ? filters.status : undefined
      };

      const { data } = await subAccountService.getSubAccounts(accountId, params);

      const subAccountsList = data.data || [];
      const paginationData = data.pagination || {};

      setSubAccounts(subAccountsList);
      setPagination(p => ({
        ...p,
        total: paginationData.total || subAccountsList.length,
        totalPages: paginationData.totalPages || Math.ceil((paginationData.total || subAccountsList.length) / pagination.limit)
      }));
    } catch (err) {
      toastService.error('Failed to load sub-accounts');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.page, debouncedSearch, filters.status, selectedAccountId]);

  useEffect(() => {
    setPagination(p => ({ ...p, page: 1 }));
  }, [debouncedSearch, filters.status, selectedAccountId]);

  useEffect(() => {
    fetchSubAccounts();
  }, [fetchSubAccounts]);

  // const handleDeleteClick = (subAccount) => {
  //   setSelectedSubAccount(subAccount);
  //   setShowDeleteModal(true);
  // };

  const handleDeleteConfirm = async () => {
    const accountId = selectedAccountId || user?.accountUID;
    if (!accountId || !selectedSubAccount) { return; }

    try {
      setSubmitting(true);
      await subAccountService.deleteSubAccount(accountId, selectedSubAccount.id);
      toastService.success('Sub-account deleted successfully');
      setShowDeleteModal(false);
      fetchSubAccounts();
    } catch (err) {
      toastService.error(err.response?.data?.message || 'Failed to delete sub-account');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) { return '—'; }
    return toLocal(date, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="flex flex-col h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden">
      <div className="flex-1 flex flex-col overflow-y-auto p-2">

        {/* Header Section */}
        <div className="border-b border-base-300 flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-6 pb-6">
          <div>
            <h1 className="page-title flex items-center gap-3">
              Sub-Account Management
            </h1>
          </div>
          {can.sub_accounts.create() && <button
            onClick={() => navigate('/sub-accounts/new')}
            className="btn-premium"
          >
            <Plus size={18} />
            <span>Create Sub-Account</span>
          </button>}
        </div>

        {/* Filters and List Area */}
        <div className="pt-6 sm:pt-8 space-y-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center glass-panel !bg-base-content/5 p-4 rounded-2xl relative z-[60]">
            <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">

              {/* Account Selector (Platform Admin Only) */}
              {!user?.accountUID && (
                <div className="relative w-full sm:w-64" ref={accountDropdownRef}>
                  <button
                    onClick={() => setIsAccountOpen(!isAccountOpen)}
                    className="flex items-center justify-between gap-2 w-full rounded-xl px-4 py-2.5 bg-base-100 border border-base-content/10 text-sm font-bold focus:ring-2 focus:ring-primary/20 focus:border-primary/50 outline-none cursor-pointer group shadow-sm transition-all"
                  >
                    <div className="flex items-center gap-2 truncate text-base-content">
                      <Building2 size={16} className="text-base-content/20 shrink-0" />
                      <span className="truncate flex-1 text-left uppercase text-[10px] tracking-wider">
                        {selectedAccountId ? (accounts.find(a => a.id === selectedAccountId)?.name || 'Select Account') : 'All Accounts'}
                      </span>
                    </div>
                    <ChevronDown size={14} className={`text-base-content/30 group-hover:text-primary transition-transform ${isAccountOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isAccountOpen && (
                    <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[200] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col max-h-60 overflow-y-auto">
                      <button
                        onClick={() => {
                          setSelectedAccountId('');
                          setIsAccountOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 ${!selectedAccountId ? CLASS_BG_PRIMARY_MUTED : ''}`}
                      >
                        <span className={`text-[11px] font-black uppercase tracking-widest ${!selectedAccountId ? CLASS_TEXT_PRIMARY : CLASS_TEXT_MUTED_HOVER}`}>
                          All Accounts
                        </span>
                        {!selectedAccountId && <Check size={12} className="text-primary ml-auto" />}
                      </button>
                      {accounts.map((acc) => (
                        <button
                          key={acc.id}
                          onClick={() => {
                            setSelectedAccountId(acc.id);
                            setIsAccountOpen(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 last:border-none ${selectedAccountId === acc.id ? CLASS_BG_PRIMARY_MUTED : ''}`}
                        >
                          <span className={`text-[11px] font-black uppercase tracking-widest ${selectedAccountId === acc.id ? CLASS_TEXT_PRIMARY : CLASS_TEXT_MUTED_HOVER}`}>
                            {acc.name}
                          </span>
                          {selectedAccountId === acc.id && <Check size={12} className="text-primary ml-auto" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Search */}
              <div className="relative flex-1 min-w-[280px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/30" size={18} />
                <input
                  type="text"
                  placeholder="Search sub-accounts"
                  className="w-full pl-10 pr-4 py-2.5 bg-base-100 border border-base-content/10 rounded-xl text-sm  focus:ring-2 focus:ring-primary focus:border-primary transition-all font-bold shadow-sm"
                  value={filters.search}
                  onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                />
              </div>


            </div>

            {/* Status Filter */}
            <div className="relative w-full sm:w-48" ref={statusDropdownRef}>
              <button
                onClick={() => setIsStatusOpen(!isStatusOpen)}
                className="flex items-center justify-between gap-2 w-full rounded-xl px-4 py-2.5 bg-base-100 border border-base-content/10 text-sm font-bold focus:ring-2 focus:ring-primary/20 focus:border-primary/50 outline-none cursor-pointer group shadow-sm transition-all"
              >
                <span className="truncate flex-1 text-left uppercase text-[10px] tracking-wider">
                  {filters.status === 'All' ? 'All Status' : filters.status}
                </span>
                <ChevronDown size={14} className={`text-base-content/30 group-hover:text-primary transition-transform ${isStatusOpen ? 'rotate-180' : ''}`} />
              </button>

              {isStatusOpen && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[200] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col">
                  {['All', 'active', 'inactive'].map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setFilters(f => ({ ...f, status }));
                        setIsStatusOpen(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 last:border-none ${filters.status === status ? CLASS_BG_PRIMARY_MUTED : ''}`}
                    >
                      <span className={`text-[11px] font-black uppercase tracking-widest ${filters.status === status ? CLASS_TEXT_PRIMARY : CLASS_TEXT_MUTED_HOVER}`}>
                        {status}
                      </span>
                      {filters.status === status && <Check size={12} className="text-primary ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Main Table Section */}
          {loading && subAccounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 space-y-4">
              <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
              <p className="text-base-content/40 font-black uppercase tracking-widest text-xs">Hydrating Sub-Accounts...</p>
            </div>
          ) : subAccounts.length === 0 ? (
            <div className="text-center py-32 glass-panel !bg-base-100/30 rounded-2xl border-dashed border-2 border-base-content/10">
              <div className="w-20 h-20 bg-base-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
                <Layers size={40} className="text-base-content/20" />
              </div>
              <h3 className="text-2xl font-black text-base-content mb-2 tracking-tight">No sub-accounts found</h3>
            </div>
          ) : (
            <div className="overflow-x-auto glass-panel rounded-2xl border border-base-content/5">
              <table className="table w-full">
                <thead>
                  <tr className="border-b border-base-content/5">
                    <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5 px-6">Sub-Account</th>
                    {!selectedAccountId && (
                      <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5">Account</th>
                    )}
                    <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5">URL</th>
                    {/* <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5">Status</th> */}
                    <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5">Created Date</th>
                    {canShowActions && (
                      <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5 px-6 text-right">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {subAccounts.map((sub) => (
                    <tr key={sub.id} className="hover:bg-base-content/5 transition-colors border-b border-base-content/5 last:border-0 group">
                      <td className="py-4 px-6">
                        <span className="font-black text-base-content group-hover:text-primary transition-colors tracking-tight">
                          {sub.name}
                        </span>
                      </td>
                      {!selectedAccountId && (
                        <td className="py-4">
                          <span className="text-sm font-bold text-base-content/70">
                            {sub.accountName || 'Unknown Account'}
                          </span>
                        </td>
                      )}
                      <td className="py-4">
                        <div className="flex items-center gap-1.5">
                          <code className="text-[10px] font-black text-base-content/40 tracking-tight bg-base-content/5 px-2 py-1 rounded-lg">
                            {sub.slug}.{process.env.REACT_APP_BASE_URL_HOSTNAME}
                          </code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`${sub.slug}.${process.env.REACT_APP_BASE_URL_HOSTNAME}`);
                              setCopiedSlug(sub.id);
                              setTimeout(() => setCopiedSlug(null), 2000);
                            }}
                            className="p-1 hover:bg-base-content/10 rounded transition-colors text-base-content/30 hover:text-primary"
                            title="Copy URL"
                          >
                            {copiedSlug === sub.id ? <Check size={12} className="text-success" /> : <Copy size={12} />}
                          </button>
                        </div>
                      </td>
                      {/* <td className="py-4">
                        <span className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-xl border ${sub.status === 'active' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' :
                            'bg-base-content/10 text-base-content/40 border-base-content/5'
                          }`}>
                          {sub.status}
                        </span>
                      </td> */}
                      <td className="py-4">
                        <span className="text-sm font-mono text-base-content/100">
                          {formatDate(sub.createdAt)}
                        </span>
                      </td>
                      {canShowActions && (
                        <td className="py-4 px-6 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {can.sub_accounts.create() && user?.scope === 'platform' && <button
                              onClick={() => navigate(`/sub-accounts/${sub.id}/clone`, { state: { sub } })}
                              className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-all text-base-content/30"
                              title="Clone Sub-Account"
                            >
                              <Copy size={18} />
                            </button>}
                            {can.sub_accounts.update() && <button
                              onClick={() => navigate(`/sub-accounts/${sub.id}/edit`, { state: { sub } })}
                              className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-all text-base-content/30"
                              title="Edit Sub-Account"
                            >
                              <Edit2 size={18} />
                            </button>}
                            {/* <button
                              onClick={() => handleDeleteClick(sub)}
                              className="p-2 hover:bg-error/10 hover:text-error rounded-lg transition-all text-base-content/30"
                              title="Delete Sub-Account"
                            >
                              <Trash2 size={18} />
                            </button> */}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-6">
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={(page) => setPagination(p => ({ ...p, page }))}
                  loading={loading}
                  total={pagination.total}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Sub-Account"
      >
        <div className="mb-6">
          <p className="text-base-content/60 text-sm leading-relaxed">
            Are you sure you want to delete <strong className="text-base-content">{selectedSubAccount?.name}</strong>?
            This will remove this business unit from <strong className="text-primary">{selectedSubAccount?.accountName}</strong>.
          </p>
        </div>
        <div className="flex gap-3 mt-8">
          <button
            onClick={() => setShowDeleteModal(false)}
            className="flex-1 px-4 py-3 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-base-200 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteConfirm}
            disabled={submitting}
            className="flex-1 px-4 py-3 bg-error text-error-content rounded-xl font-bold text-xs uppercase tracking-widest hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? 'Deleting...' : 'Confirm Delete'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default SubAccounts;
